import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DeleteOutlined, EditOutlined, ArrowRightOutlined, CalendarOutlined, UserOutlined, StarFilled, StarOutlined, ClockCircleOutlined, DownOutlined, UpOutlined, TeamOutlined } from '@ant-design/icons';
import { Popconfirm, Typography, Button, Avatar, Tag, Tooltip } from 'antd';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';
import ProgressBadge from './ProgressBadge';

const { Text } = Typography;

/**
= * 
 * @param {object} action - داده‌های اقدام
 * @param {function} onClick - تابع کلیک روی کارت
 * @param {function} onDelete - تابع حذف اقدام
 * @param {function} onEdit - تابع ویرایش اقدام
 * @param {string} className - کلاس‌های اضافی
 * @param {boolean} showActions - نمایش دکمه‌های عملیات
 * @param {boolean} showDates - نمایش تاریخ‌ها
 * @param {boolean} showProgress - نمایش درصد پیشرفت
 * @param {boolean} isBookmarked - آیا اقدام نشان‌گذاری شده است
 * @param {Array<string>} myRoles - نقش‌های کاربر در این اقدام (برای نمایش دکمه‌های پذیرش/بستن)
 * @param {function} onAccept - تابع پذیرش اقدام
 * @param {function} onRequestClose - تابع ارسال درخواست بستن
 * @param {function} onClose - تابع بستن اقدام
 * @param {boolean} acceptLoading - وضعیت لودینگ پذیرش
 * @param {boolean} requestCloseLoading - وضعیت لودینگ درخواست بستن
 * @param {boolean} closeLoading - وضعیت لودینگ بستن
 * @param {function} onBookmark - تابع بوکمارک/آنبوکمارک کردن اقدام
 * @param {boolean} bookmarkLoading - وضعیت لودینگ بوکمارک
 */
const ActionCard = ({
  action,
  onClick,
  onDelete,
  onEdit,
  className = '',
  showActions = true,
  showDates = true,
  showProgress = true,
   isBookmarked = false,
   myRoles = [],
   onAccept,
   onRequestClose,
   onClose,
   acceptLoading = false,
   requestCloseLoading = false,
   closeLoading = false,
   onBookmark,
   bookmarkLoading = false,
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionMaxLength = 100;
  
  if (!action) return null;

  const handleCardClick = (e) => {
    if (e.target.closest('button') || e.target.closest('.ant-popconfirm')) {
      return;
    }
    if (onClick) onClick(action);
  };


  const percent = action.percent_complete !== null && action.percent_complete !== undefined 
    ? Number(action.percent_complete) 
    : null;

  const hasResponsibleRole = Array.isArray(myRoles) && myRoles.includes('مسئول_اجرا');
  const hasAccountableRole = Array.isArray(myRoles) && myRoles.includes('پاسخگو');

  const showRoleActions = (hasResponsibleRole && (onAccept || onRequestClose)) || (hasAccountableRole && onClose);

  return (
    <div
      onClick={handleCardClick}
      className={`
        group relative bg-white dark:bg-gray-800
        border border-gray-200 dark:border-gray-700
        rounded-xl p-4
        transition-all duration-300 ease-out
        hover:shadow-md hover:shadow-gray-200/50 dark:hover:shadow-gray-900/50
        hover:border-gray-300 dark:hover:border-gray-600
        cursor-pointer
        w-full
        ${className}
      `}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3
              className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
              style={{ fontFamily: 'CustomFont' }}
            >
              {action.title || 'بدون عنوان'}
            </h3>
            {/* {isBookmarked && (
              <StarFilled className="text-yellow-400 text-sm flex-shrink-0 mt-0.5" />
            )} */}
          </div>
          {action.description && (
            <div className="mt-1">
              <Text
                className="text-xs text-gray-500 dark:text-gray-400"
                style={{ fontFamily: 'CustomFont' }}
              >
                {action.description.length > descriptionMaxLength && !isDescriptionExpanded
                  ? action.description.substring(0, descriptionMaxLength) + '...'
                  : action.description}
              </Text>
              {action.description.length > descriptionMaxLength && (
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsDescriptionExpanded(!isDescriptionExpanded);
                  }}
                  className="p-0 h-auto text-[10px] text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                  style={{ fontFamily: 'CustomFont', padding: 0, height: 'auto', marginTop: 2 }}
                >
                  {isDescriptionExpanded ? (
                    <>
                      <UpOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                      بستن
                    </>
                  ) : (
                    <>
                      <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
                      مشاهده بیشتر
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {showRoleActions && (
            <>
              {hasResponsibleRole && onAccept && action.is_accepted === false && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAccept();
                  }}
                  disabled={acceptLoading}
                  className="px-2.5 py-1 text-[10px] rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'CustomFont' }}
                  title="پذیرش اقدام"
                >
                  {acceptLoading ? '...' : 'پذیرش'}
                </button>
              )}
              {hasResponsibleRole && onRequestClose && action.has_close_request === false && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestClose();
                  }}
                  disabled={requestCloseLoading}
                  className="px-2.5 py-1 text-[10px] rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-sm hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'CustomFont' }}
                  title="درخواست بستن"
                >
                  {requestCloseLoading ? '...' : 'درخواست بستن'}
                </button>
              )}
              {hasAccountableRole && onClose && action.status !== 'بسته' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  disabled={closeLoading}
                  className="px-2.5 py-1 text-[10px] rounded-full bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'CustomFont' }}
                  title="بستن اقدام"
                >
                  {closeLoading ? '...' : 'بستن اقدام'}
                </button>
              )}
            </>
          )}
          {showActions && (
            <>
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onEdit) onEdit(action);
                  }}
                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                  title="ویرایش"
                >
                  <EditOutlined />
                </button>
              )}
              {onDelete && action.can_delete !== false && (
                <Popconfirm
                  title="حذف اقدام"
                  description="آیا از حذف این اقدام مطمئن هستید؟"
                  okText="حذف"
                  cancelText="انصراف"
                  okButtonProps={{ danger: true }}
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    if (onDelete) onDelete(action);
                  }}
                  onCancel={(e) => {
                    e?.stopPropagation();
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="حذف"
                  >
                    <DeleteOutlined />
                  </button>
                </Popconfirm>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="flex items-center gap-2">
          {action.status && (
            <StatusBadge status={action.status} size="sm" />
          )}
          {action.priority && (
            <PriorityBadge priority={action.priority} size="sm" />
          )}
          {action.action_type && (
            <span
              className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800"
              style={{ fontFamily: 'CustomFont' }}
            >
              {action.action_type}
            </span>
          )}
        </div>

        <div className="hidden md:block w-px h-4 bg-gray-200 dark:bg-gray-700" />

        {showProgress && percent !== null && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-gray-400 font-medium" style={{ fontFamily: 'CustomFont', minWidth: '35px' }}>
              {percent}٪
            </span>
            <div className="relative w-24 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${
                  percent < 30 
                    ? 'bg-red-500 dark:bg-red-600' 
                    : percent < 70 
                    ? 'bg-amber-500 dark:bg-amber-600' 
                    : 'bg-emerald-500 dark:bg-emerald-600'
                }`}
                style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
              />
            </div>
          </div>
        )}

        {action.requester && showProgress && (
          <div className="hidden md:block w-px h-4 bg-gray-200 dark:bg-gray-700" />
        )}

        {action.requester && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <UserOutlined className="text-[11px] text-gray-400" />
            <span className="text-gray-500 dark:text-gray-500" style={{ fontFamily: 'CustomFont' }}>درخواست‌کننده:</span>
            <span style={{ fontFamily: 'CustomFont' }}>
              {action.requester.name || action.requester.email || 'نامشخص'}
            </span>
          </div>
        )}

        {action.requester && (
          <div className="hidden md:block w-px h-4 bg-gray-200 dark:bg-gray-700" />
        )}

        <div className="flex items-center gap-1.5 text-xs">
          {myRoles.length > 0 ? (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-gray-500 dark:text-gray-500" style={{ fontFamily: 'CustomFont' }}>نقش من:</span>
              {myRoles.map((role, index) => {
                const roleColors = {
                  'پاسخگو': 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
                  'مسئول_اجرا': 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
                  'مطلع': 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
                  'مشاور': 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
                };
                const roleColor = roleColors[role] || 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700';
                
                return (
                  <span
                    key={index}
                    className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${roleColor}`}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {role}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500 dark:text-gray-500" style={{ fontFamily: 'CustomFont' }}>نقش من:</span>
              <span className="text-amber-600 dark:text-amber-400 font-medium" style={{ fontFamily: 'CustomFont' }}>
                نیاز به تخصیص نقش
              </span>
            </div>
          )}
        </div>
      </div>

      {showDates && (action.planned_start_date || action.planned_end_date || action.actual_start_date || action.actual_end_date || action.created_at || action.updated_at) && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(action.planned_start_date || action.planned_end_date) && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <CalendarOutlined className="text-[11px] text-blue-500" />
                  <Text
                    type="secondary"
                    style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                  >
                    برنامه‌ریزی:
                  </Text>
                </div>
                <div className="flex flex-col gap-0.5 pr-5">
                  {action.planned_start_date && (
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                      شروع: {action.planned_start_date}
                    </Text>
                  )}
                  {action.planned_end_date && (
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                      پایان: {action.planned_end_date}
                    </Text>
                  )}
                </div>
              </div>
            )}

            {(action.actual_start_date || action.actual_end_date) && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <CalendarOutlined className="text-[11px] text-emerald-500" />
                  <Text
                    type="secondary"
                    style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                  >
                    واقعی:
                  </Text>
                </div>
                <div className="flex flex-col gap-0.5 pr-5">
                  {action.actual_start_date && (
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                      شروع: {action.actual_start_date}
                    </Text>
                  )}
                  {action.actual_end_date && (
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                      پایان: {action.actual_end_date}
                    </Text>
                  )}
                </div>
              </div>
            )}

            {(action.created_at || action.updated_at) && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <ClockCircleOutlined className="text-[11px] text-gray-500" />
                  <Text
                    type="secondary"
                    style={{ fontFamily: 'CustomFont', fontSize: 11 }}
                  >
                    اطلاعات:
                  </Text>
                </div>
                <div className="flex flex-col gap-0.5 pr-5">
                  {action.created_at && (
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                      ایجاد: {action.created_at}
                    </Text>
                  )}
                  {action.updated_at && (
                    <Text style={{ fontFamily: 'CustomFont', fontSize: 10 }}>
                      ویرایش: {action.updated_at}
                    </Text>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {action.racis && action.racis.length > 0 && (
        <div className="pt-2 border-t border-gray-100 dark:border-gray-800 mt-3">
          <div className="flex items-center gap-2 mb-2">
            <TeamOutlined className="text-[12px] text-blue-500" />
            <Text
              type="secondary"
              style={{ fontFamily: 'CustomFont', fontSize: 11, fontWeight: 500 }}
            >
              تیم RACI:
            </Text>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {action.racis.map((raci) => {
              const roleColors = {
                'پاسخگو': { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' },
                'مسئول_اجرا': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200 dark:border-emerald-800' },
                'مطلع': { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200 dark:border-purple-800' },
                'مشاور': { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' },
              };
              const roleColor = roleColors[raci.raci_role] || { bg: 'bg-gray-50 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
              
              return (
                <Tooltip
                  key={raci.id}
                  title={
                    <div style={{ fontFamily: 'CustomFont' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{raci.user?.name || 'نامشخص'}</div>
                      <div style={{ fontSize: 11 }}>نقش: {raci.raci_role}</div>
                      {raci.user?.email && (
                        <div style={{ fontSize: 11, opacity: 0.8 }}>{raci.user.email}</div>
                      )}
                    </div>
                  }
                >
                  <div
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border ${roleColor.bg} ${roleColor.border} ${roleColor.text} transition-all hover:shadow-sm cursor-default`}
                  >
                    <Avatar
                      size={20}
                      src={raci.user?.profile_img}
                      style={{ flexShrink: 0 }}
                    >
                      {raci.user?.name ? String(raci.user.name).charAt(0) : '?'}
                    </Avatar>
                    <Text
                      style={{
                        fontFamily: 'CustomFont',
                        fontSize: 11,
                        fontWeight: 500,
                        maxWidth: 100,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {raci.user?.name || 'نامشخص'}
                    </Text>
                    <Tag
                      size="small"
                      style={{
                        fontFamily: 'CustomFont',
                        fontSize: 9,
                        margin: 0,
                        padding: '0 6px',
                        height: 18,
                        lineHeight: '18px',
                        borderRadius: 4,
                      }}
                      className={roleColor.bg}
                    >
                      {raci.raci_role}
                    </Tag>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
          <span style={{ fontFamily: 'CustomFont' }}>مشاهده جزئیات</span>
          <ArrowRightOutlined className="text-[10px]" />
        </div>

        {onBookmark && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onBookmark();
            }}
            disabled={bookmarkLoading}
            className={`p-1.5 rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
              isBookmarked
                ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
                : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20'
            }`}
            title={isBookmarked ? 'حذف از بوکمارک' : 'افزودن به بوکمارک'}
          >
            {isBookmarked ? (
              <StarFilled className="text-base" />
            ) : (
              <StarOutlined className="text-base" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

ActionCard.propTypes = {
  action: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.string,
    priority: PropTypes.string,
    action_type: PropTypes.string,
    percent_complete: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    planned_start_date: PropTypes.string,
    planned_end_date: PropTypes.string,
    actual_start_date: PropTypes.string,
    actual_end_date: PropTypes.string,
    requester: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    racis: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        raci_role: PropTypes.string,
        user: PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          name: PropTypes.string,
          email: PropTypes.string,
          profile_img: PropTypes.string,
        }),
      })
    ),
  }).isRequired,
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  className: PropTypes.string,
  showActions: PropTypes.bool,
  showDates: PropTypes.bool,
  showProgress: PropTypes.bool,
  isBookmarked: PropTypes.bool,
  myRoles: PropTypes.arrayOf(PropTypes.string),
  onAccept: PropTypes.func,
  onRequestClose: PropTypes.func,
  onClose: PropTypes.func,
  acceptLoading: PropTypes.bool,
  requestCloseLoading: PropTypes.bool,
  closeLoading: PropTypes.bool,
  onBookmark: PropTypes.func,
  bookmarkLoading: PropTypes.bool,
};

export default ActionCard;
